// @ts-check
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import moment from "moment-timezone";
import { auth } from "@helloaudio/auth";
import { isString } from "lodash";
import { makeUrl } from "./apiCall";

const FIRST_TIME_EVENT_MIN_STRIPE_DATE = "2026-01-20 00:00:00";

/**
 * @param {string | Date} stripeCreationDate
 * @returns {Date | null}
 */
const toDateFromStripeCreationDate = (stripeCreationDate) => {
  if (moment.isDate(stripeCreationDate)) {
    return Number.isNaN(stripeCreationDate.getTime()) ? null : stripeCreationDate;
  }

  if (isString(stripeCreationDate)) {
    const trimmed = stripeCreationDate.trim();
    if (trimmed) {
      const parsed = moment(trimmed);
      if (parsed.isValid()) {
        return parsed.toDate();
      }
    }
  }

  return null;
};

/**
 * Determine if a user should be eligible for first-time tracking events based on stripe creation date.
 * @param {string | Date | null} stripeCreationDate The stripe creation date from the DB
 * @returns {boolean} True when the user is eligible
 */
export const isStripeCreationDateEligible = (stripeCreationDate) => {
  if (stripeCreationDate) {
    const minStripeCreationDate = toDateFromStripeCreationDate(FIRST_TIME_EVENT_MIN_STRIPE_DATE);
    if (minStripeCreationDate) {
      const parsed = toDateFromStripeCreationDate(stripeCreationDate);
      if (parsed) {
        return parsed.getTime() >= minStripeCreationDate.getTime();
      }
    }
  }
  return false;
};

/**
 * Send event data to the tracking API.
 * @param {string} event Event name
 * @param {{[key: string]: string | number}} [data = {}] Event data to send
 * @returns {Promise<void>} Promise that resolves when the request is complete
 */
export const sendTrackingData = async (event, data = {}) => {
  const endpoint = makeUrl("/track");
  const params = { event, ...data };

  try {
    /** @type {Pick<import('axios').AxiosRequestHeaders, 'Authorization'>} */
    const headers = {};
    const isAuthenticated = await auth.isAuthenticated();
    if (isAuthenticated) {
      const token = await auth.getIdToken();
      headers.Authorization = `Bearer ${token}`;
    }
    const response = await axios.get(endpoint, { headers, params });
    return response.data;
  } catch (error) {
    console.error("Error sending tracking data:", error);
  }
};

/**
 * Track Meta Pixel events when available.
 * @param {string} event Event name
 * @param {{[key: string]: string | number}} [data]
 */
export const trackFbqEvent = (event, data) => {
  if (typeof window !== "undefined") {
    /** @type {any} */
    const w = window;
    if (typeof w.fbq === "function") {
      w.fbq("track", event, data);
    }
  }
};

/**
 * Send pageview tracking data to the tracking API.
 */
export function usePageView() {
  const location = useLocation();

  useEffect(() => {
    const trackPageView = () => {
      sendTrackingData("pageview", {
        external: 0,
        pathname: location.pathname,
        search: location.search,
        timestamp: new Date().toISOString(),
      });
    };

    trackPageView();
  }, [location]);
}
